/**
 * Thème de l'interface (UI-02) : Clair / Sombre / **Auto** (suit le système).
 * « Auto » = aucune valeur mémorisée → on suit `prefers-color-scheme`, comme le
 * script anti-flash de `index.html`. Le choix explicite est mémorisé.
 */
export const THEMES = ['light', 'dark', 'auto'] as const;
export type ThemeMode = (typeof THEMES)[number];

const KEY = 'gnosis-theme';

const systemDark = (): boolean => matchMedia('(prefers-color-scheme: dark)').matches;

/** Mode courant : la valeur mémorisée, sinon « auto ». */
export function getTheme(): ThemeMode {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'light' || v === 'dark' ? v : 'auto';
  } catch {
    return 'auto';
  }
}

/** Applique le mode au document (classe `dark`). */
export function applyTheme(mode: ThemeMode): void {
  const dark = mode === 'auto' ? systemDark() : mode === 'dark';
  document.documentElement.classList.toggle('dark', dark);
}

/** Change le mode : « auto » oublie le choix, les autres le mémorisent. */
export function setTheme(mode: ThemeMode): void {
  try {
    if (mode === 'auto') localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, mode);
  } catch {
    /* stockage indisponible : le mode tient pour la session */
  }
  applyTheme(mode);
}

/** En mode auto, suit les changements du système. Renvoie le désabonnement. */
export function watchSystemTheme(): () => void {
  const mq = matchMedia('(prefers-color-scheme: dark)');
  const onChange = (): void => {
    if (getTheme() === 'auto') applyTheme('auto');
  };
  mq.addEventListener('change', onChange);
  return () => mq.removeEventListener('change', onChange);
}
